"use client";

import { PromotionalEventType } from "@faire/web-api--source/indigofair/common/PromotionalEventType";
import Color from "@faire/web/slate/Color";
import { MarketPrimaryLogo } from "@faire/web/slate/icons/faire-markets/MarketPrimaryLogo";
import { MarketSecondaryLogo } from "@faire/web/slate/icons/faire-markets/MarketSecondaryLogo";
import { SummerMarketPrimaryLogo } from "@faire/web/slate/icons/faire-markets/SummerMarketPrimaryLogo";
import { WinterMarketPrimaryLogo } from "@faire/web/slate/icons/faire-markets/WinterMarketPrimaryLogo";
import { WinterMarketSecondaryLogo } from "@faire/web/slate/icons/faire-markets/WinterMarketSecondaryLogo";
import {
  IAutoDimensionProps,
  withAutoDimension,
} from "@faire/web/slate/Theme/withAutoDimension";
import React from "react";

import { getEventTheme } from "./eventsThemeColors";

interface IEventLogoProps extends IAutoDimensionProps {
  isInverse?: boolean;
  iconColor?: Color;
}

type eventKeys =
  | "faireMarket"
  | Extract<
      PromotionalEventType,
      | PromotionalEventType.FAIRE_MARKET_JULY_2023
      | PromotionalEventType.FAIRE_WINTER_MARKET_2023
      | PromotionalEventType.FAIRE_SUMMER_MARKET_2022
    >;
type logoKeys = "primary" | "secondary";

const PrimaryMarketBase: React.FC<IEventLogoProps> = ({
  height,
  width,
  isInverse,
  iconColor,
  className,
}) => {
  return (
    <MarketPrimaryLogo
      isInverse={isInverse}
      height={height}
      width={width}
      iconColor={iconColor}
      className={className}
    />
  );
};

const PrimaryMarketBaseFsm23: React.FC<IEventLogoProps> = ({
  height,
  width,
  isInverse,
  iconColor,
  className,
}) => {
  return (
    <MarketPrimaryLogo
      isInverse={isInverse}
      height={height}
      width={width}
      iconColor={
        iconColor ??
        getEventTheme(PromotionalEventType.FAIRE_MARKET_JULY_2023).badgeIcon
      }
      className={className}
    />
  );
};

const PrimaryMarketBaseFwm23: React.FC<IEventLogoProps> = ({
  height,
  width,
  isInverse,
  iconColor,
  className,
}) => {
  return (
    <WinterMarketPrimaryLogo
      isInverse={isInverse}
      height={height}
      width={width}
      iconColor={iconColor}
      className={className}
    />
  );
};

const PrimaryMarketBaseFsm22: React.FC<IEventLogoProps> = ({
  height,
  width,
  isInverse,
  iconColor,
  className,
}) => {
  return (
    <SummerMarketPrimaryLogo
      height={height}
      width={width}
      lightBackground={!isInverse}
      sunColor={iconColor}
      className={className}
    />
  );
};

const PrimaryMarket = withAutoDimension(PrimaryMarketBase);
const PrimaryMarketFsm23 = withAutoDimension(PrimaryMarketBaseFsm23);
const PrimaryMarketFwm23 = withAutoDimension(PrimaryMarketBaseFwm23);
const PrimaryMarketFsm22 = withAutoDimension(PrimaryMarketBaseFsm22);

const SecondaryMarketBase: React.FC<IEventLogoProps> = ({
  height,
  width,
  isInverse,
  iconColor,
  className,
}) => (
  <MarketSecondaryLogo
    height={height}
    width={width}
    isInverse={isInverse}
    iconColor={iconColor}
    className={className}
  />
);

const SecondaryMarketBaseFsm23: React.FC<IEventLogoProps> = ({
  height,
  width,
  isInverse,
  iconColor,
  className,
}) => (
  <MarketSecondaryLogo
    height={height}
    width={width}
    isInverse={isInverse}
    iconColor={
      iconColor ??
      getEventTheme(PromotionalEventType.FAIRE_MARKET_JULY_2023).badgeIcon
    }
    className={className}
  />
);

const SecondaryMarketBaseFwm23: React.FC<IEventLogoProps> = ({
  height,
  width,
  isInverse,
  iconColor,
  className,
}) => (
  <WinterMarketSecondaryLogo
    height={height}
    width={width}
    isInverse={isInverse}
    iconColor={iconColor}
    className={className}
  />
);

const SecondaryMarketBaseFsm22: React.FC<IEventLogoProps> = ({
  height,
  width,
  isInverse,
  iconColor,
  className,
}) => (
  <SummerMarketPrimaryLogo
    height={height}
    width={width}
    lightBackground={!isInverse}
    sunColor={iconColor}
    className={className}
  />
);

const SecondaryMarket = withAutoDimension(SecondaryMarketBase);
const SecondaryMarketFsm23 = withAutoDimension(SecondaryMarketBaseFsm23);
const SecondaryMarketFwm23 = withAutoDimension(SecondaryMarketBaseFwm23);
const SecondaryMarketFsm22 = withAutoDimension(SecondaryMarketBaseFsm22);

export const EventsThemeLogo: {
  [event in eventKeys]: {
    [logo in logoKeys]: React.FC<IEventLogoProps>;
  };
} = {
  faireMarket: {
    primary: PrimaryMarket,
    secondary: SecondaryMarket,
  },
  FAIRE_MARKET_JULY_2023: {
    primary: PrimaryMarketFsm23,
    secondary: SecondaryMarketFsm23,
  },
  FAIRE_WINTER_MARKET_2023: {
    primary: PrimaryMarketFwm23,
    secondary: SecondaryMarketFwm23,
  },
  FAIRE_SUMMER_MARKET_2022: {
    primary: PrimaryMarketFsm22,
    secondary: SecondaryMarketFsm22,
  },
};
